import {
    ApiShopperLandingPageLink,
    ApiContentSection,
    ApiProductGridType,
    ApiProductSortOption,
    ApiSectionType,
    DEFAULT_PRODUCT_SORT_OPTION,
} from '@storefronts/common-types'
import { getLinkPrices } from './priceFormat'
import type { ApiPromotion } from '@storefronts/common-types'

type SortableSection = Extract<
    ApiContentSection,
    { type: ApiSectionType.PLP | ApiSectionType.PERSONALIZED_PLP }
>

export const getSectionDefaultSort = (
    section: SortableSection,
): ApiProductSortOption => {
    if (section.type === ApiSectionType.PERSONALIZED_PLP) {
        return 'recommended'
    }

    if (section.productGridType === ApiProductGridType.RECENTLY_VIEWED) {
        return 'recommended'
    }

    if (!section.productGridType && section.defaultSortOrder) {
        return section.defaultSortOrder
    }

    return DEFAULT_PRODUCT_SORT_OPTION
}

export const sortProducts = <TLink extends ApiShopperLandingPageLink>(
    products: TLink[],
    sortOption: ApiProductSortOption,
    discount: ApiPromotion | null,
): TLink[] => {
    if (!products || products.length === 0) return products

    const sortedProducts = [...products]

    switch (sortOption) {
        case 'recommended':
            return sortedProducts

        case 'best-selling':
            return sortedProducts.sort((a, b) => {
                const rankA = a.quantityOrderedRank ?? Infinity
                const rankB = b.quantityOrderedRank ?? Infinity

                // Products without rank (Infinity) go to the end
                if (rankA === Infinity && rankB === Infinity) {
                    return 0 // Maintain original order
                }
                if (rankA === Infinity) {
                    return 1 // A goes to the end
                }
                if (rankB === Infinity) {
                    return -1 // B goes to the end
                }

                // Sort by rank ascending (rank 1 = best, rank 2 = second, etc.)
                if (rankA !== rankB) {
                    return rankA - rankB
                }

                // Tiebreaker: highest price first
                const priceA = getLinkPrices(a, discount).price
                const priceB = getLinkPrices(b, discount).price
                return priceB - priceA
            })

        case 'price-low-high':
            return sortedProducts.sort((a, b) => {
                const priceA = getLinkPrices(a, discount).price
                const priceB = getLinkPrices(b, discount).price
                return priceA - priceB
            })

        case 'price-high-low':
            return sortedProducts.sort((a, b) => {
                const priceA = getLinkPrices(a, discount).price
                const priceB = getLinkPrices(b, discount).price
                return priceB - priceA
            })

        case 'newest-first':
            return sortedProducts.sort((a, b) => {
                const dateA = a.productCreatedAt
                    ? new Date(a.productCreatedAt).getTime()
                    : 0
                const dateB = b.productCreatedAt
                    ? new Date(b.productCreatedAt).getTime()
                    : 0
                return dateB - dateA
            })

        case 'star-rating':
            return sortedProducts.sort((a, b) => {
                const ratingA = a.reviews?.rating
                const ratingB = b.reviews?.rating
                const countA = a.reviews?.count ?? 0
                const countB = b.reviews?.count ?? 0

                // Products with no reviews/ratings go to the end
                // Note: rating of 0 is valid (zero stars), only null/undefined means no rating
                const hasRatingA = ratingA != null
                const hasRatingB = ratingB != null

                if (!hasRatingA && !hasRatingB) {
                    // Both have no ratings, maintain original order
                    return 0
                }
                if (!hasRatingA) {
                    // A has no rating, put it at the end
                    return 1
                }
                if (!hasRatingB) {
                    // B has no rating, put it at the end
                    return -1
                }

                // Sort by rating descending (5 > 4.9 > 4.8 > ...)
                if (ratingB !== ratingA) {
                    return ratingB - ratingA
                }

                // Break ties by review count (higher count first)
                if (countB !== countA) {
                    return countB - countA
                }

                // Break remaining ties by highest price first
                const priceA = getLinkPrices(a, discount).price
                const priceB = getLinkPrices(b, discount).price
                return priceB - priceA
            })

        case 'alphabetical-a-z':
            return sortedProducts.sort((a, b) => {
                return a.title.localeCompare(b.title)
            })

        case 'alphabetical-z-a':
            return sortedProducts.sort((a, b) => {
                return b.title.localeCompare(a.title)
            })

        default:
            return sortedProducts
    }
}
